import { isEqual } from 'lodash'
import { getUserListData, _getUDataById } from '../users/mockData'
import { getSplitListDataByTeamMemberId } from '../splits/mockData'
import {
  getAccountListData,
  getAccountListDataByOwnerId,
} from '../accounts/mockData'
import { getTeamMemberLastSelectedStateDataByTeamMemberId } from '../teamMemberLastSelectedStates/mockData'
import {
  getOpportunityListData,
  getOpportunityListDataByCompanyAccountOwnerId,
  getOpportunityListDataByOwnerAndAssignedToId,
  getOpportunityWithClosedWonServicesOnUnsignedContractsListData,
} from '../opportunities/mockData'
import {
  getCompanyListData,
  getCompanyListDataByAccountOwnerId,
} from '../companies/mockData'
import {
  getOrderListDataByAssignedToId,
  getUnassignedOrderListData,
} from '../orders/mockData'
import { getOrderServiceOrderListDataByTenantProductionManagerId } from '../orderServiceOrders/mockData'
import { getCallListDataByTeamMemberId } from '../calls/mockData'
import { getEmailListDataByTeamMemberId } from '../emails/mockData'
import { getMeetingListDataByTeamMemberId } from '../meetings/mockData'
import { getTaskListDataByTeamMemberId } from '../tasks/mockData'
import { getFromEmailNeedsReviewCaseListData } from '../cases/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import {
  TeamMemberRoleIds,
  getTeamMemberRoleById,
} from '~/services/selectLists/teamMemberRoles'
import { TeamMember } from '~/cynqueModules/dataObjects/TeamMember'
import type {
  ITeamMember,
  ITeamMemberOverview,
  ITeamMemberSelectItem,
} from '@/types/teamMember/models'
import type { TEntityId, ISelectItem, IEntitySelectItem } from '~/types/models'

const teamMemberListData = undefined as unknown as ITeamMember[]

export const getTeamMemberListData = getListDataFunctionFactory<ITeamMember[]>({
  listData: teamMemberListData,
  storageKey: 'teammembers',
  getStartingListData: getStartingTeamMemberListData,
})

export function _getTMDataById(
  id: TEntityId,
  list: ITeamMember[]
): ITeamMember {
  if (!list) {
    throw new Error(`_getTMData called with undefined/null list`)
  }
  const tm = list.find((teamMember: ITeamMember) => teamMember.id === id)
  if (!tm) {
    throw new Error(`Can't find teamMember '${id}'`)
  }
  return tm
}
export function _getTMSelectItemDataById(
  id: TEntityId,
  list: ITeamMember[]
): IEntitySelectItem | undefined {
  if (!list) {
    throw new Error(`_getTMSelectItemDataById called with undefined/null list`)
  }
  const teamMember = list.find((tm: ITeamMember) => tm.id === id)
  if (!teamMember) {
    return undefined
  }
  const processedTeamMember = Object.assign(
    teamMember,
    TeamMember(teamMember)
  ) as ITeamMemberSelectItem
  return {
    id: processedTeamMember.id,
    label: processedTeamMember.label,
  }
}

export async function getTeamMemberDataById(
  id: TEntityId
): Promise<ITeamMember> {
  const list: ITeamMember[] = await getTeamMemberListData()
  return _getTMDataById(id, list)
}

export async function getTeamMemberSelectItemDataById(
  id: string
): Promise<ISelectItem | undefined> {
  const list = await getTeamMemberListData()
  return _getTMSelectItemDataById(id, list)
}

export async function getTeamMemberListDataByPersonId(id: TEntityId) {
  const list = await getTeamMemberListData()
  return list.filter((teamMember: ITeamMember) =>
    isEqual(teamMember.person.id, id)
  )
}

export async function getTeamMemberDataForUserId(
  id: string
): Promise<ITeamMember | undefined> {
  const list: ITeamMember[] = await getTeamMemberListData()
  if (!list) {
    throw new Error(`getTeamMemberList returned undefined/null`)
  }
  return list.find((tm) => tm.user?.id === id)
}

function _hasRole(teamMember: ITeamMember, roleId: TeamMemberRoleIds) {
  return (
    teamMember.defaultRole?.id === roleId ||
    (teamMember.defaultRoles ?? []).some((role) => role.id === roleId)
  )
}

// cynque-api-server#1802, by the server's rules: on a Sales Manager's overview
// only, what the OTHER Sales Reps own -- accounts and companies by account
// owner, opportunities by owner or assigned-to -- less any opportunity the
// manager is also on. Role tests read the default role or the default roles,
// as TeamMember()'s isSalesManager does.
async function _getSalesRepListDataBySalesManagerId(
  id: TEntityId
): Promise<
  Pick<
    ITeamMemberOverview,
    'salesRepAccounts' | 'salesRepCompanies' | 'salesRepOpportunities'
  >
> {
  const teamMemberList = await getTeamMemberListData()
  const salesManager = _getTMDataById(id, teamMemberList)
  if (!_hasRole(salesManager, TeamMemberRoleIds.SALES_MANAGER)) {
    return {
      salesRepAccounts: [],
      salesRepCompanies: [],
      salesRepOpportunities: [],
    }
  }
  const salesRepIds = new Set(
    teamMemberList
      .filter(
        (teamMember) =>
          teamMember.id !== id &&
          _hasRole(teamMember, TeamMemberRoleIds.SALES_REP)
      )
      .map((teamMember) => teamMember.id)
  )
  const isSalesRep = (teamMemberId: TEntityId | undefined) =>
    teamMemberId !== undefined && salesRepIds.has(teamMemberId)
  return {
    salesRepAccounts: (await getAccountListData()).filter((account) =>
      isSalesRep(account.owner?.id)
    ),
    salesRepCompanies: (await getCompanyListData()).filter((company) =>
      isSalesRep(company.account?.owner?.id)
    ),
    salesRepOpportunities: (await getOpportunityListData()).filter(
      (opportunity) =>
        (isSalesRep(opportunity.owner?.id) ||
          isSalesRep(opportunity.assignedTo?.id)) &&
        opportunity.owner?.id !== id &&
        opportunity.assignedTo?.id !== id
    ),
  }
}

export async function getTeamMemberOverviewDataById(
  id: string
): Promise<ITeamMemberOverview | undefined> {
  const theTeamMemberData = await getTeamMemberDataById(id)
  if (!theTeamMemberData) {
    throw new Error(`TeamMember data for ${id} not found`)
  }
  const theOverviewData = {
    teamMember: theTeamMemberData,
    splits: await getSplitListDataByTeamMemberId(id),
    accounts: await getAccountListDataByOwnerId(id),
    companies: await getCompanyListDataByAccountOwnerId(id),
    opportunities: await getOpportunityListDataByOwnerAndAssignedToId(id),
    opportunitiesWithClosedWonServicesOnUnsignedContracts:
      await getOpportunityWithClosedWonServicesOnUnsignedContractsListData(),
    ...(await _getSalesRepListDataBySalesManagerId(id)),
    projects: [],
    orders: await getOrderListDataByAssignedToId(id),
    ordersToBeAssigned: await getUnassignedOrderListData(),
    orderServiceOrders:
      await getOrderServiceOrderListDataByTenantProductionManagerId(id),
    serviceRequests: [],
    cases: [],
    casesWithStatusFromEmailNeedsReview:
      await getFromEmailNeedsReviewCaseListData(),
    // No Service Request entity on the server yet -- it returns [] (#1741).
    serviceRequestsWithStatusFromEmailNeedsReview: [],
    tasks: await getTaskListDataByTeamMemberId(id),
    calls: await getCallListDataByTeamMemberId(id),
    emails: await getEmailListDataByTeamMemberId(id),
    meetings: await getMeetingListDataByTeamMemberId(id),
    lastSelectedState: await getTeamMemberLastSelectedStateDataByTeamMemberId(
      id
    ),
  }
  return theOverviewData
}

// Note: Race condition or circular reference issue...Can't get
// mock data to return correctly using 'getPersonDataById' function.
// Have to hard-code it. Might be possible by making all mock data
// functions async, but not worth the time to try.
//
// Additional Note: Have now made all mock data methods async, but
// have not gone back to see if race condition has been mitigated.

export async function getStartingTeamMemberListData(): Promise<ITeamMember[]> {
  const ulist = await getUserListData()
  return [
    // Case Bot. Stamped as the author on rows Cynque creates itself -- the
    // email-ingested case notes and documents -- so that "every note has an
    // author" stays an enforced invariant instead of a nullable column. See
    // WXTG/cynque-api-server#1600.
    //
    // ⚠ Named for the WRITER: more bots are expected, one per automated writer,
    // which is why `isSystem` is a flag rather than a well-known id.
    //
    // ⚠ NO `user`, deliberately: nobody can log in as it.
    // ⚠ `isSystem` is on the PERSON and nowhere else -- "not a human" is a fact
    //   about the identity, and a team member is a binding of one. The server
    //   embeds the whole person in the team member (a `composed` embed), which
    //   is what isSystemTeamMember reads. Do not add a copy here: the two could
    //   then disagree, and nothing would catch it.
    // ⚠ Filtered out of isOfferableTeamMember, so it is never offered in a
    //   team-member picker. It DOES appear on the Team Members list page, which
    //   reads raw getTeamMembers -- hiding it there would make an unexplained
    //   author appear on notes with no row to explain it.
    // ⚠ `defaultRoles: []` is the honest statement: it holds no roles. The
    //   legacy single `defaultRole` is required by ITeamMember and none of the
    //   twelve fits; ENGINEER is chosen only because it drives none of the
    //   derived isOrderAdministrator / isSalesManager / isCommissionManager /
    //   isCasesManager / isServiceRequestsManager flags. The real seeded role is a server question -- #1600.
    {
      id: 'XXTM0000',
      hrid: 'TM0000',
      person: {
        id: 'XXPE0000',
        hrid: 'PE0000',
        isSystem: true,
        name: {
          firstName: 'Case',
          lastName: 'Bot',
        },
        phone: '',
        cell: '',
        email: '',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      // 'CB' to match the initials Name() computes from Case Bot.
      avatar: { descr: 'CB', type: 'teamMember' },
      email: '',
      phone: '',
      defaultRole: getTeamMemberRoleById(TeamMemberRoleIds.ENGINEER),
      defaultRoles: [],
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTM0001',
      hrid: 'TM0001',
      person: {
        id: 'XXPE0018',
        hrid: 'PE0018',
        name: {
          firstName: 'Meryl',
          lastName: 'Streep',
        },
        phone: '(510) 343-3234',
        cell: '(777) 999-8860',
        email: 'meryl@streep.org',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      user: _getUDataById('XXU0005', ulist),
      avatar: { descr: 'MS', type: 'teamMember' },
      numProjects: 19,
      numOrders: 34,
      numAccounts: 3,
      email: 'mstreep@wxtechnologygroup.com',
      phone: '(510) 333-5555',
      defaultRole: getTeamMemberRoleById(TeamMemberRoleIds.SALES_REP),
      defaultRoles: [
        getTeamMemberRoleById(TeamMemberRoleIds.SALES_REP),
        // getTeamMemberRoleById(TeamMemberRoleIds.ORDER_ADMINISTRATOR),
        getTeamMemberRoleById(TeamMemberRoleIds.SERVICEORDER_MANAGER),
        getTeamMemberRoleById(TeamMemberRoleIds.SALES_MANAGER),
        // Shows the Home Operations "Cases Needing Review" card in dev.
        getTeamMemberRoleById(TeamMemberRoleIds.CASES_MANAGER),
      ],
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTM0002',
      hrid: 'TM0002',
      avatar: { descr: 'HA', type: 'teamMember' },
      person: {
        id: 'XXPE0034',
        hrid: 'PE0034',
        name: {
          firstName: 'House',
          lastName: 'Accounts',
        },
        phone: '',
        email: 'house@accounts.org',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      numProjects: 6,
      numOrders: 14,
      numAccounts: 8,
      email: 'houseaccounts@wxtechnologygroup.com',
      phone: '(510) 333-1000',
      defaultRole: getTeamMemberRoleById(TeamMemberRoleIds.SALES_REP),
      defaultRoles: [getTeamMemberRoleById(TeamMemberRoleIds.SALES_REP)],
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTM0003',
      hrid: 'TM0003',
      avatar: { descr: 'BC', type: 'teamMember' },
      person: {
        id: 'XXPE0035',
        hrid: 'PE0035',
        name: {
          firstName: 'Benedict',
          lastName: 'Cumberbatch',
        },
        phone: '510-842-4444',
        email: 'benedicth@cumberbatch.org',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      numProjects: 10,
      numOrders: 4,
      numAccounts: 2,
      email: 'bcumberbatch@wxtechnologygroup.com',
      phone: '(510) 575-4444',
      defaultRole: getTeamMemberRoleById(TeamMemberRoleIds.SALES_SUPPORT),
      defaultRoles: [
        getTeamMemberRoleById(TeamMemberRoleIds.SALES_SUPPORT),
        // Also a Sales Rep, so that Meryl Streep's Sales Manager view has his
        // account and opportunity in its "Sales Reps' …" cards
        // (cynque-api-server#1802).
        getTeamMemberRoleById(TeamMemberRoleIds.SALES_REP),
      ],
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXTM0004',
      hrid: 'TM0004',
      avatar: { descr: 'SJ', type: 'teamMember' },
      person: {
        id: 'XXPE0036',
        hrid: 'PE0036',
        name: {
          firstName: 'Scarlett',
          lastName: 'Johansson',
        },
        phone: '510-742-6666',
        email: 'scarlett@johansson.org',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: true,
      },
      numProjects: 34,
      numOrders: 24,
      numAccounts: 10,
      email: 'shohansson@wxtechnologygroup.com',
      phone: '(510) 664-6666',
      defaultRole: getTeamMemberRoleById(TeamMemberRoleIds.SOLUTION_ENGINEER),
      defaultRoles: [
        getTeamMemberRoleById(TeamMemberRoleIds.SOLUTION_ENGINEER),
      ],
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXTM0005',
      hrid: 'TM0005',
      avatar: { descr: 'RD', type: 'teamMember' },
      person: {
        id: 'XXPE0037',
        hrid: 'PE0037',
        name: {
          firstName: 'Robert',
          lastName: 'Downy',
          suffix: 'Jr.',
        },
        phone: '510-642-5432',
        email: 'robert@downeyjr.org',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: true,
      },
      numProjects: 4,
      numOrders: 23,
      numAccounts: 12,
      email: 'rdownyjr@wxtechnologygroup.com',
      phone: '(510) 333-5432',
      defaultRole: getTeamMemberRoleById(TeamMemberRoleIds.SUB_AGENT),
      defaultRoles: [getTeamMemberRoleById(TeamMemberRoleIds.SUB_AGENT)],
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXTM0006',
      hrid: 'TM0006',
      avatar: { descr: 'ST', type: 'teamMember' },
      person: {
        id: 'XXPE0038',
        hrid: 'PE0038',
        name: {
          firstName: 'Service',
          lastName: 'Technician',
        },
        phone: '510-111-1000',
        email: 'service@technician.org',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: true,
      },
      numProjects: 0,
      numOrders: 0,
      numAccounts: 0,
      email: 'service@wxtechnologygroup.com',
      phone: '(510) 111-1000',
      defaultRole: getTeamMemberRoleById(TeamMemberRoleIds.ENGINEER),
      defaultRoles: [getTeamMemberRoleById(TeamMemberRoleIds.ENGINEER)],
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXTM0007',
      hrid: 'TM0007',
      avatar: { descr: 'AH', type: 'teamMember' },
      person: {
        id: 'XXPE0039',
        hrid: 'PE0039',
        name: {
          firstName: 'Anthony',
          lastName: 'Hopkins',
        },
        phone: '510-542-1112',
        email: 'anthony@hopkins.org',
        status: {
          id: 2,
          label: 'Active',
        },
        version: '1',
        canBeDeleted: false,
      },
      numProjects: 19,
      numOrders: 30,
      numAccounts: 12,
      email: 'ahopkins@wxtechnologygroup.com',
      phone: '(510) 575-1112',
      defaultRole: getTeamMemberRoleById(TeamMemberRoleIds.SALES_REP),
      defaultRoles: [getTeamMemberRoleById(TeamMemberRoleIds.SALES_REP)],
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
  ]
}
